import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import './VaultCapMeter.css';

const RECHECK_MS = 60 * 1000;

function ago(ms, lang) {
  // A clock running ahead never says "in 2 minutes".
  const seconds = Math.min(0, Math.round((ms - Date.now()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'auto' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  return rtf.format(Math.round(seconds / 86400), 'day');
}

/**
 * Settings → Vault → Cloud copy: "Last synced …" and the vault key
 * (Standalone vault card 6.2) — from `vault_sync_status` (`lastSyncedAt`,
 * `keyEpoch`, `passphraseSetAt`). Shown on a device whose vault syncs.
 */
export default function VaultSyncFacts() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);

  const read = useCallback(async () => {
    try {
      setStatus(await invoke('vault_sync_status'));
    } catch {
      // Keep the last answer.
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    read();
    // A minute is fine for "last synced"; no event import, so every settings
    // test that renders this page keeps working without a Tauri event mock.
    const timer = setInterval(read, RECHECK_MS);
    return () => clearInterval(timer);
  }, [read]);

  if (!status?.enrolled) return null;
  const lang = i18n.language;
  // A clean cycle can still hold things back (a newer keyblock, a locked vault
  // with staged rows, an oversize or refused item). Then the vault's own
  // banners speak, and "Last synced just now" would be wrong next to them.
  const heldBack = !!status.lastError || !!status.updateRequired || (status.staged || 0) > 0;
  const setAt = status.passphraseSetAt ? new Date(status.passphraseSetAt) : null;

  return (
    <div className="vault-cap-meter" data-testid="vault-sync-facts">
      {!heldBack && (
        <div className="vault-cap-meter-label">
          {status.lastSyncedAt
            ? t('vault.factsLastSynced', 'Last synced {{when}}', { when: ago(status.lastSyncedAt, lang) })
            : t('vault.factsNotSynced', 'Not synced yet on this device')}
        </div>
      )}
      {status.keyEpoch != null && (
        <div className="vault-cap-meter-hint">
          {setAt && !Number.isNaN(setAt.getTime())
            ? t('vault.factsKey', 'Encryption key {{epoch}} · master passphrase set {{date}}', {
                epoch: status.keyEpoch,
                date: setAt.toLocaleDateString(lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
              })
            : t('vault.factsKeyOnly', 'Encryption key {{epoch}}', { epoch: status.keyEpoch })}
        </div>
      )}
    </div>
  );
}
